<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <warehouse-purchase-order-dialog
      v-model="purchaseOrderDialog"
      :editedItem="editedItem"
      :is-purchase-order="purchaseOrder"
    >
    </warehouse-purchase-order-dialog>
    <warehouse-entry-grocery-dialog
      v-model="purchaseItemDialog"
      :editedItem="editedItem"
      @groceryAdded="addToWarehouse"
    >
    </warehouse-entry-grocery-dialog>
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem">
    </warehouse-waybill-dialog>
    <warehouse-write-off-dialog v-model="writeOffDialog" :edited-item="editedItem">
    </warehouse-write-off-dialog>
    <warehouse-return-dialog v-model="returnDialog" :edited-item="editedItem">
    </warehouse-return-dialog>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <!-- Mobile Cards grouped by warehouse -->
      <template v-if="!loading && translatedInventory.length > 0">
        <div
          v-for="group in inventoryByWarehouse"
          :key="group.warehouseId"
          class="mb-4 ordera-box-shadow"
        >
          <mobile-data-cards
            :headers="headers"
            :items="group.items"
            :loading="false"
            :title="toLatin(group.warehouseName)"
            :bold-fields="['quantityWithMeasure']"
            :fields-per-row="3"
            :show-row-number="false"
            show-filters
            hide-labels
            :bottom-padding="false"
          >
          </mobile-data-cards>
        </div>
      </template>

      <!-- Loading state -->
      <v-progress-linear
        v-else-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <!-- Empty state -->
      <empty-state
        v-else
        icon="mdi-archive-outline"
        :title="$t('no_inventory')"
        :subtitle="$t('no_inventory_yet')"
      >
      </empty-state>

      <!-- Mobile Bottom Navigation -->
      <v-bottom-navigation fixed grow class="elevation-8 mb-2">
        <v-menu offset-y top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="green accent-4" text>
              <span>{{ $t("order") }}</span>
              <v-icon>mdi-chevron-up</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="orderGroceries(false)">
              <v-list-item-title>{{ $t("warehouse.request_quote") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="orderGroceries(true)">
              <v-list-item-title>{{ $t("warehouse.purchase_order") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn color="primary" text @click="openPurchaseDialog">
          <span>{{ $t("add_warehouse") }}</span>
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-btn color="blue" text @click="openWaybillDialog">
          <span>{{ $t("warehouse.release") }}</span>
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn color="orange" text @click="openReturnDialog">
          <span>{{ $t("warehouse.return") }}</span>
          <v-icon>mdi-arrow-u-left-top</v-icon>
        </v-btn>
        <v-btn color="error" text @click="openWriteOffDialog">
          <span>{{ $t("warehouse.write-off") }}</span>
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </v-bottom-navigation>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredInventory.length > 0 || loading || hasInventoryItems"
        :headers="desktopHeaders"
        :items="filteredInventory"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <autocomplete
              :label="$t('status')"
              :items="stockStatusItems"
              v-model="stockStatusFilter"
              item-text="text"
              item-value="value"
              hide-details
              dense
              class="mr-2"
              style="max-width: 250px"
            ></autocomplete>
            <v-spacer></v-spacer>
            <warehouse-button-order
              @order="orderGroceries($event)"
            ></warehouse-button-order>
            <warehouse-button-import-articles
              @click="openPurchaseDialog"
            ></warehouse-button-import-articles>
            <warehouse-button-waybill
              @click="openWaybillDialog"
            ></warehouse-button-waybill>
            <warehouse-button-return
              class="mr-2"
              @click="openReturnDialog"
            ></warehouse-button-return>
            <warehouse-button-trash @click="openWriteOffDialog"></warehouse-button-trash>
          </v-toolbar>
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredInventory.length === 0 && !hasInventoryItems"
        icon="mdi-archive-outline"
        :title="$t('no_inventory')"
        :subtitle="$t('no_inventory_yet')"
      >
        <div class="d-flex flex-wrap justify-center gap-2">
          <warehouse-button-order
            @order="orderGroceries($event)"
          ></warehouse-button-order>
          <warehouse-button-import-articles
            @click="openPurchaseDialog"
          ></warehouse-button-import-articles>
          <warehouse-button-waybill @click="openWaybillDialog"></warehouse-button-waybill>
          <warehouse-button-return @click="openReturnDialog"></warehouse-button-return>
        </div>
      </empty-state>
    </template>
  </div>
</template>

<script>
import WarehouseEntryGroceryDialog from "@/components/warehouse/entry/WarehouseEntryGroceryDialog.vue";
import {
  WarehousePurchaseOrderDialog,
  WarehouseWaybillDialog,
} from "@/components/warehouse/requirement";
import {
  WarehouseButtonOrder,
  WarehouseButtonWaybill,
  WarehouseButtonTrash,
  WarehouseButtonImportArticles,
  WarehouseButtonReturn,
} from "@/components/warehouse/buttons";
import {
  documentGrocery,
  orderDocumentGrocery,
  requestOrderDocumentGrocery,
} from "@/models/warehouse";
import WarehouseWriteOffDialog from "@/components/warehouse/writeOff/dialogs/WarehouseWriteOffDialog.vue";
import WarehouseReturnDialog from "@/components/warehouse/return/dialogs/WarehouseReturnDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    WarehouseEntryGroceryDialog,
    WarehousePurchaseOrderDialog,
    WarehouseWaybillDialog,
    WarehouseButtonOrder,
    WarehouseButtonWaybill,
    WarehouseButtonTrash,
    WarehouseButtonImportArticles,
    WarehouseButtonReturn,
    WarehouseWriteOffDialog,
    WarehouseReturnDialog,
    EmptyState,
    MobileDataCards,
  },
  data: () => ({
    purchaseOrderDialog: false,
    waybillDialog: false,
    writeOffDialog: false,
    returnDialog: false,
    purchaseItemDialog: false,
    purchaseOrder: false,
    inventory: [],
    editedItem: null,
    loading: false,
    stockStatusFilter: "all",
    cachedGroceries: null,
    cachedInventory: null,
    lastFetchTime: null,
  }),

  created() {
    this.editedItem = this.resetEditedItem();
    this.fetchInventory();
  },

  methods: {
    resetEditedItem() {
      return {
        ...documentGrocery,
        groceries: [],
        document_date: new Date().toISOString().split("T")[0],
      };
    },
    openDialog() {
      this.editedItem = {
        ...this.resetEditedItem(),
        warehouse: this.filters.warehouse[0],
      };
    },
    openPurchaseDialog() {
      this.openDialog();
      this.purchaseItemDialog = true;
    },
    openWaybillDialog() {
      this.openDialog();
      this.waybillDialog = true;
    },
    openWriteOffDialog() {
      this.openDialog();
      this.writeOffDialog = true;
    },
    openReturnDialog() {
      this.openDialog();
      this.returnDialog = true;
    },
    addToWarehouse(grocery) {
      this.inventory.push(grocery);
      this.showSuccessSnackbar(this.$t("grocery_added_to_warehouse"));
    },
    buildInventoryFromData(warehouseStatusData, groceriesData = null) {
      // New format: array of warehouses with groceries
      const inventoryMap = new Map();

      // Build map from warehouse status data
      warehouseStatusData.forEach((warehouse) => {
        warehouse.groceries.forEach((grocery) => {
          const key = `${grocery.id}-${warehouse.id}`;
          inventoryMap.set(key, {
            warehouseId: warehouse.id,
            warehouseName: warehouse.name,
            groceryId: grocery.id,
            groceryName: grocery.name,
            measure: grocery.measure,
            quantity: grocery.total,
          });
        });
      });

      if (groceriesData) {
        // If we have all groceries data, show all items including those with 0 quantity
        return this.filters.warehouse.flatMap((warehouse) =>
          groceriesData.map((grocery) => {
            const key = `${grocery.id}-${warehouse.id}`;
            const inventoryItem = inventoryMap.get(key);
            return {
              id: `${grocery.id}-${warehouse.id}`,
              code: grocery.code,
              quantity: inventoryItem?.quantity || 0,
              warehouse: { id: warehouse.id, name: warehouse.name },
              grocery: {
                id: grocery.id,
                name: grocery.name,
                measure: grocery.measure,
                min_quantity: grocery.min_quantity,
              },
              measure: grocery.measure,
            };
          })
        );
      }

      // Convert map to inventory array format
      const result = [];
      warehouseStatusData.forEach((warehouse) => {
        warehouse.groceries.forEach((grocery) => {
          result.push({
            id: `${grocery.id}-${warehouse.id}`,
            code: grocery.code || "",
            quantity: grocery.total,
            warehouse: { id: warehouse.id, name: warehouse.name },
            grocery: {
              id: grocery.id,
              name: grocery.name,
              measure: grocery.measure,
              min_quantity: grocery.min_quantity || null,
            },
            measure: grocery.measure,
          });
        });
      });

      return result;
    },
    async fetchInventory(forceRefresh = false) {
      this.loading = true;
      try {
        const CACHE_TTL = 5 * 60 * 1000;
        const now = Date.now();

        if (
          !forceRefresh &&
          this.cachedInventory &&
          this.lastFetchTime &&
          now - this.lastFetchTime < CACHE_TTL
        ) {
          this.applyFiltering();
          this.loading = false;
          return;
        }

        const needsAllGroceries =
          this.stockStatusFilter === "all" ||
          this.stockStatusFilter === "out_of_stock" ||
          this.stockStatusFilter === "minimum";

        const warehouseIds = this.filters.warehouse.map((w) => w.id).join(",");
        const date = this.filters.dates.from;

        const [warehouseStatusResponse, groceriesResponse] = await Promise.all([
          warehouseService.getWarehouseStatus({ warehouse_ids: warehouseIds, date }),
          needsAllGroceries
            ? warehouseService.getGroceriesWithWarehouse()
            : Promise.resolve({ data: [] }),
        ]);

        // Cache the raw data
        this.cachedInventory = warehouseStatusResponse.data;
        if (needsAllGroceries) {
          this.cachedGroceries = groceriesResponse.data;
        }
        this.lastFetchTime = now;

        // Build inventory from new format
        this.inventory = this.buildInventoryFromData(
          warehouseStatusResponse.data,
          needsAllGroceries ? groceriesResponse.data : null
        );
      } catch (error) {
        console.error("Error loading inventory:", error);
        this.showErrorSnackbar(this.$t("error_loading_inventory"));
      } finally {
        this.loading = false;
      }
    },
    applyFiltering() {
      const needsAllGroceries =
        this.stockStatusFilter === "all" ||
        this.stockStatusFilter === "out_of_stock" ||
        this.stockStatusFilter === "minimum";
      this.inventory = this.buildInventoryFromData(
        this.cachedInventory,
        needsAllGroceries ? this.cachedGroceries : null
      );
    },
    editItem(item) {
      this.editedItem = { ...item };
      this.purchaseOrderDialog = true;
    },
    orderGroceries(isPurchaseOrder) {
      this.purchaseOrder = isPurchaseOrder;
      this.editedItem = isPurchaseOrder
        ? { ...orderDocumentGrocery }
        : { ...requestOrderDocumentGrocery };
      this.purchaseOrderDialog = true;
    },
  },
  computed: {
    stockStatusItems() {
      return [
        { text: this.$t("all_items"), value: "all" },
        { text: this.$t("in_stock"), value: "in_stock" },
        { text: this.$t("minimum_quantity"), value: "minimum" },
        { text: this.$t("out_of_stock"), value: "out_of_stock" },
      ];
    },
    translatedInventory() {
      return this.selectedWarehouseInventory.map((item) => {
        const translatedMeasure = this.translate(item.grocery.measure);
        return {
          ...item,
          warehouse: { ...item.warehouse, name: this.translate(item.warehouse.name) },
          grocery: {
            ...item.grocery,
            name: this.translate(item.grocery.name),
            measure: translatedMeasure,
          },
          measure: translatedMeasure,
          quantityWithMeasure: `${item.quantity} ${translatedMeasure}`,
        };
      });
    },
    filteredInventory() {
      let filtered = this.customSearch(
        this.translatedInventory,
        this.headers,
        this.filters.search
      ).sort((a, b) => b.quantity - a.quantity);

      const filterMap = {
        in_stock: (item) => item.quantity > 0,
        out_of_stock: (item) => item.quantity === 0,
        minimum: (item) =>
          Number(item.grocery.min_quantity) &&
          item.quantity < Number(item.grocery.min_quantity),
      };

      const filterFn = filterMap[this.stockStatusFilter];
      return filterFn ? filtered.filter(filterFn) : filtered;
    },
    headers() {
      return [
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("code"), value: "code", cols: 3 },
        { text: this.$t("warehouse.product"), cols: 5, value: "grocery.name" },
        { text: this.$t("quantity"), value: "quantity", mobile: false },
        {
          text: this.$t("minimum_quantity"),
          value: "grocery.min_quantity",
          mobile: false,
        },
        {
          text: this.$t("unit_of_measurment"),
          value: "grocery.measure",
          mobile: false,
          width: "150px",
        },
        {
          text: this.$t("quantity"),
          value: "quantityWithMeasure",
          desktop: false,
          cols: 4,
        },
      ];
    },
    desktopHeaders() {
      return this.headers.filter((h) => h.desktop !== false);
    },
    inventoryByWarehouse() {
      const grouped = {};
      this.translatedInventory.forEach((item) => {
        const warehouseId = item.warehouse.id;
        if (!grouped[warehouseId]) {
          grouped[warehouseId] = {
            warehouseId,
            warehouseName: item.warehouse.name,
            items: [],
          };
        }
        grouped[warehouseId].items.push(item);
      });
      return Object.values(grouped);
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    selectedWarehouseInventory() {
      if (!this.filters.warehouse.length) return this.inventory;

      const warehouseIds = new Set(this.filters.warehouse.map((w) => w.id));
      return this.inventory.filter((item) => warehouseIds.has(item.warehouse.id));
    },
    exportDate() {
      return (
        new Date(this.filters.dates.from).toLocaleDateString("rs-RS", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        }) + "."
      );
    },
    exportTitle() {
      const warehouseName =
        this.filters.warehouse.map((w) => w.name).join(", ") || this.$t("stock");
      return `${warehouseName} ${this.$t("state").toLowerCase()}`;
    },
    hasInventoryItems() {
      return this.inventory.some((item) => item.quantity > 0);
    },
    exportData() {
      return this.filteredInventory;
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.fetchInventory(true);
    },
    "filters.warehouse": {
      handler() {
        this.fetchInventory(true);
      },
      deep: true,
    },
    "filters.dates.from"() {
      this.fetchInventory(true);
    },
    stockStatusFilter() {
      this.fetchInventory();
    },
    purchaseOrderDialog(val) {
      if (!val) {
        this.editedItem = this.resetEditedItem();
      }
    },
    purchaseItemDialog(val) {
      if (!val) {
        this.editedItem = this.resetEditedItem();
      }
    },
    waybillDialog(val) {
      if (!val) {
        this.editedItem = this.resetEditedItem();
      }
    },
    writeOffDialog(val) {
      if (!val) {
        this.editedItem = this.resetEditedItem();
      }
    },
    returnDialog(val) {
      if (!val) {
        this.editedItem = this.resetEditedItem();
      }
    },
    isExcelDownloading(val) {
      if (val) {
        this.downloadExcel({
          fileName: `${this.exportTitle}.xlsx`,
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.exportData,
              title: this.exportTitle,
              subtitle: this.exportDate,
            },
          ],
        });
      }
    },
    isPrinting(val) {
      if (val) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.exportDate,
          tables: [{ headers: this.headers, items: this.exportData }],
        });
      }
    },
  },
};
</script>
